/**
 * WhatIs browser-source entrypoint. Wires WS → reducer → Three.js scenes + DOM
 * overlays. Root `<div>` gets `data-scene` + `window.__sj_scene` on every
 * transition. URL params: `key` (API key), `ws` (port, default 3001),
 * `mock=1` (offline replay), `dev=1` (debug overlays), `cam=top-left|…`.
 * @packageDocumentation
 */

import { Object3D } from 'three';
import type { CameraPosition, OutMsg } from '@sg/shared';
import { readViewerMode, wsUrlFor } from '@sg/shared';
import { WsClient } from './ws/client.js';
import { resolveWsBase } from './ws/ws-base.js';
import { Store } from './state/store.js';
import type { GameState, SceneName } from './state/types.js';
import { createStage } from './scene/stage.js';
import { createBoard, BOARD_COLS_MAX, BOARD_ROWS } from './scene/board.js';
import { createPollOverlay } from './scene/poll-overlay.js';
import { createClueZoom } from './scene/clue-zoom.js';
import { createAnswerReveal } from './scene/answer-reveal.js';
import { createLeaderboard } from './scene/leaderboard.js';
import { createRoundTransition } from './scene/round-transition.js';
import { createPodium } from './scene/podium.js';
import { createCalibration } from './scene/calibration-prompt.js';
import { createConnectionLost } from './scene/connection-lost.js';
import { createLobby } from './scene/lobby.js';
import { createDelayIndicator } from './scene/delay-indicator.js';
import { createCameraPlaceholder } from './scene/camera-placeholder.js';
import {
  centerOf,
  computeSafeArea,
  getCameraRect,
  getLeaderboardRect,
  parseCameraOverride,
} from './scene/lib/safe-area.js';
import { applySceneVisibility } from './scene/scene-visibility.js';
import { createMockBus } from './dev/mock-ws.js';
import { sfx as sfxImpl } from './audio/sfx.js';
import { music as musicImpl } from './audio/music.js';
import { audioBus } from './audio/bus.js';
import { mountFullscreenButton } from './ui/fullscreen-button.js';
import { mountAudioUnlock } from './ui/audio-unlock.js';
import { createAnimatedBg } from './ui/animated-bg.js';
import { createSparkles } from './ui/sparkles.js';
import { createInstructions } from './ui/instructions.js';
import { createSideLeaderboard } from './ui/side-leaderboard.js';
import { createCountdown } from './ui/countdown.js';
import { createRoundSplash } from './ui/round-splash.js';
import { createDomStage } from './ui/stage.js';
import { createCategoryVote } from './ui/category-vote.js';
import { createValueVote } from './ui/value-vote.js';
import { createGuessBubbles } from './ui/guess-bubbles.js';
import { createConflictBanner } from './ui/conflict-banner.js';
import { escapeInline } from './ui/polish.js';
import { applyTheme, DEFAULT_THEME } from './ui/theme.js';
import { mountWatchQrWidget } from '../../../shared/qrcode-generator/watch-qr.js';

/** Slug of THIS browser source — used to filter `cross_game_conflict` events. */
const MY_SLUG = 'whatis';

// Apply default theme so CSS vars render before the first state_snapshot.
applyTheme(DEFAULT_THEME);

const params = new URL(window.location.href).searchParams;
const mockMode = params.get('mock') === '1';
// Viewer mode (`?viewer=1&handle=<HANDLE>`) routes through the public read-only
// /watch/<handle>/feed socket and suppresses streamer-only chrome (audio,
// fullscreen button, instructions, connection-lost overlay). When absent we
// fall back to streamer/OBS mode using the legacy `?key=...` query param.
const viewerMode = readViewerMode(params);
const isViewer = viewerMode.kind === 'viewer';
const key = viewerMode.kind === 'streamer' ? viewerMode.key : 'dev-no-key';
const wsPort = Number(params.get('ws') ?? '3001');

const devOverlay = params.get('dev') === '1';
// URL override always wins over per-tenant setting (used for testing).
const camOverride = parseCameraOverride(window.location.search);

const root = document.getElementById('root')!;
root.innerHTML = '';
root.style.position = 'relative';
root.style.width = '100vw';
root.style.height = '100vh';
root.style.overflow = 'hidden';
root.setAttribute('data-testid', 'browser-root');

// DOM stage: fixed 1920x1080, CSS-transformed to fit viewport; see ui/stage.ts.
const domStage = createDomStage({ mount: root });
const stageEl = domStage.el;
// Animated background sits behind everything inside the stage.
const animatedBg = createAnimatedBg(stageEl);

const stage = createStage({ mount: stageEl });
const board = createBoard();
const pollOverlay = createPollOverlay();
const clueZoom = createClueZoom();
const answerReveal = createAnswerReveal();
const leaderboard = createLeaderboard(stageEl);
const roundTransition = createRoundTransition();
const podium = createPodium();
const calibration = createCalibration();
const cameraPlaceholder = createCameraPlaceholder();
const connectionLost = createConnectionLost(stageEl);
const lobby = createLobby(stageEl);
const delayIndicator = createDelayIndicator(stageEl);
const instructions = createInstructions(stageEl);
const sideLeaderboard = createSideLeaderboard(stageEl);
const countdown = createCountdown(stageEl);
const sparkles = createSparkles(stageEl);
const roundSplash = createRoundSplash(stageEl);
// DOM poll overlays for both phases; board hidden beneath via scene-visibility.
const categoryVote = createCategoryVote(stageEl);
const valueVote = createValueVote(stageEl);
// Rising chat-bubble overlay during answer/grace; mirrors the cross-game pattern.
const guessBubbles = createGuessBubbles(stageEl);
// Cross-game conflict banner — backend fans `cross_game_conflict` to every
// connected browser source for the tenant; we filter by MY_SLUG below.
const conflictBanner = createConflictBanner(stageEl);
// Last-won category drives the clue_arm tile-flip lookup below.
let pickedCategory: string | null = null;
// Track the most recently armed cell so we can mark it consumed on
// score_update (after the answer reveal completes). Without this the board
// never dims spent tiles — populateBoard runs once per round, and used-state
// otherwise lives only in backend `usedFlags` on poll_open.
let lastArmedCell: { col: number; row: number } | null = null;

// Viewer-visible help strip; host-only commands (!start/!stop/!skip/!abort) omitted.
const HELP_COMMANDS = ['!a <answer>', '!score', '!leaderboard', '!rules'];
instructions.setCommands(HELP_COMMANDS);
sideLeaderboard.setVisible(false);
countdown.hide();
void animatedBg;

// Streamer-only chrome (audio + fullscreen + help strip + delay/calibration
// indicators + connection-lost overlay). The public watch SPA owns those
// concerns at the iframe wrapper level — see games/watch/browser/src/viewer.ts.
// Iframe stays muted (autoplay-blocked is the right default; the watch SPA can
// ship a tap-to-unmute overlay later if wanted) and decorative chrome that
// would compete with the SPA shell is hidden.
// Viewer-aware audio facades: in viewer mode, every sfx.play / music.play call
// is a no-op so the AudioContext is never instantiated and Chromium never logs
// "AudioContext was not allowed to start". Streamer mode forwards to the real
// implementations untouched.
const sfx: Pick<typeof sfxImpl, 'play' | 'preload'> = isViewer
  ? { play: () => {}, preload: async () => {} }
  : sfxImpl;
const music: Pick<typeof musicImpl, 'play'> = isViewer
  ? { play: () => {} }
  : musicImpl;

if (!isViewer) {
  // Audio: pre-fetch + decode all SFX buffers so the first cue has no hitch.
  // Music streams on demand. The bus stays locked until a user gesture (or OBS
  // page-permission) fires audioBus.unlock() — see ui/audio-unlock.ts.
  void sfx.preload();
  mountFullscreenButton();
  mountAudioUnlock();
  // Lobby music starts immediately; if the bus is locked the HTMLAudio play()
  // promise rejects silently and music.resumeIfPending() picks it up later.
  music.play('lobby');
} else {
  // Hide streamer-only chrome: top/bottom instruction strips, delay HUD,
  // calibration overlay. The Three.js groups for connectionLost / calibration
  // stay in the scene graph but never become visible because we never trigger
  // their show() paths in viewer mode.
  instructions.topEl.style.display = 'none';
  instructions.bottomEl.style.display = 'none';
  delayIndicator.el.style.display = 'none';
  calibration.hide();
}

// Default layout; reapplied by applyCameraLayout once camera_position known.
// Board x=-320 (Apr 2026): -280 crowded the LB, -370 cut money tiles off-screen.
board.group.position.set(-320, -80, 0);
pollOverlay.group.position.set(-320, 0, 5);
leaderboard.group.position.set(650, -10, 0);
stage.scene.add(board.group);
stage.scene.add(pollOverlay.group);
stage.scene.add(leaderboard.group);
stage.scene.add(clueZoom.group);
stage.scene.add(answerReveal.group);
stage.scene.add(roundTransition.group);
stage.scene.add(podium.group);
stage.scene.add(calibration.group);
stage.scene.add(cameraPlaceholder.group);

// Board stays empty until the FSM enters a round; hidden in lobby state.
board.group.visible = false;

// 5 categories + 5 value rungs; keep in sync with round-fsm.ts r1 values.
// Round 2 + final-round mechanics removed Apr 2026 (parked for Survey Says).
let lastRound: 'r1' | 'r2' | 'final' | 'results' | 'lobby' = 'lobby';
let boardHasContent = false;
let lastLobbyLb: unknown = null;

function valuesForRound(_round: 'r1' | 'r2' | 'final' | 'results' | 'lobby'): number[] {
  return [200, 400, 600, 800, 1000];
}

function populateBoard(categories: string[], round: 'r1' | 'r2' | 'final' | 'results' | 'lobby'): void {
  // Column count is implicit in categories.length (set by backend per-tenant
  // categories_per_round, 3 or 6). Clamp to MAX so a malformed snapshot can't
  // overflow the pre-allocated mesh pool. Empty string fills any short tail
  // so we never end up with `undefined` headers on screen.
  const desired = Math.min(BOARD_COLS_MAX, Math.max(1, categories.length));
  const cats = categories.slice(0, desired);
  while (cats.length < desired) cats.push('');
  board.setContent(cats, valuesForRound(round));
  board.resetTiles();
  board.playReveal();
  boardHasContent = true;
}

const store = new Store();
const state0 = store.getState();

// --- WS wiring ---
let wsClient: WsClient | null = null;
let watchQrCtrl: ReturnType<typeof mountWatchQrWidget> | null = null;
let showWatchQr = true;
let currentHandle: string | null = null;

function applyWatchQrVisibility(handle: string | null): void {
  if (isViewer) return;
  if (!showWatchQr) {
    if (watchQrCtrl) { watchQrCtrl.destroy(); watchQrCtrl = null; }
    return;
  }
  if (watchQrCtrl) {
    watchQrCtrl.update(handle);
  } else {
    watchQrCtrl = mountWatchQrWidget({ handle });
  }
}

function handleWs(msg: OutMsg): void {
  // Dispatch to reducer first (fast, synchronous state update).
  store.dispatch({ type: 'ws', msg });

  // Side effects: schedule reveal-at-ms bound animations, SFX cues, etc.
  switch (msg.type) {
    case 'clue_arm': {
      sfx.play('clue-reveal');
      instructions.setInstruction('CLUE ARMED', 'Get ready — the clue is about to drop!', 'default');
      // Jeopardy-style flip + zoom on the chosen tile; clue-zoom then takes over.
      const cat = pickedCategory ?? msg.cluePreview.category;
      const col = board.columnOfCategory(cat);
      const row = board.rowOfValue(msg.cluePreview.value);
      if (col >= 0 && row >= 0) {
        lastArmedCell = { col, row };
        void board.flipTile(col, row);
      } else {
        lastArmedCell = null;
      }
      break;
    }
    case 'clue_reveal': {
      const revealLocal = wsClient ? wsClient.clock.toLocalMs(msg.reveal_at_ms) : msg.reveal_at_ms;
      const hardEndLocal = wsClient ? wsClient.clock.toLocalMs(msg.hard_end_ms) : msg.hard_end_ms;
      const graceEndLocal = wsClient ? wsClient.clock.toLocalMs(msg.grace_end_ms) : msg.grace_end_ms;
      // Answers become acceptable after the broadcast-relay delay; the visible
      // ANSWER countdown holds the full hard window until that local time, then
      // ticks down to hard_end. Aligns with backend's clue_active_start_ms.
      const clueActiveStartLocal = revealLocal + msg.broadcast_delay_seconds * 1000;
      clueZoom.setClue(msg.clue, revealLocal, hardEndLocal, graceEndLocal);
      countdown.setPhase('Answer', hardEndLocal, clueActiveStartLocal);
      sfx.play('clue-type');
      instructions.setInstruction(
        'WHAT IS IT?',
        `Type your answer for ${msg.clue.value} points — <code>!a &lt;your answer&gt;</code>`,
        'answer',
      );
      sparkles.burst({ x: 960, y: 220, count: 18, speed: 0.8 });
      break;
    }
    case 'grace_begin': {
      const graceEndLocal = wsClient ? wsClient.clock.toLocalMs(msg.grace_end_ms) : msg.grace_end_ms;
      clueZoom.enterGrace(graceEndLocal);
      countdown.setPhase('Grace', graceEndLocal);
      sfx.play('timer-warn');
      instructions.setInstruction('GRACE WINDOW', 'Last chance — late answers scored at floor value', 'answer');
      break;
    }
    case 'answer_reveal':
      // Hide clue-zoom so its dollar ticker doesn't bleed under the answer card.
      clueZoom.hide();
      countdown.hide();
      answerReveal.show(msg.display_answer, msg.top_scorers);
      sfx.play('answer-sting');
      if (msg.top_scorers.length > 0) {
        const top = msg.top_scorers[0]!;
        // Hero callout uses the answer + points awarded — NOT the player name.
        // Chat can be busy and shouting one name on screen feels noisy; the
        // side leaderboard already names+scores everyone who landed a point.
        instructions.setInstruction(
          `${msg.display_answer.toUpperCase()} +${top.points}`,
          `Correct answer: <code>${escapeInline(msg.display_answer)}</code>`,
          'celebrate',
        );
        sparkles.burst({ x: 960, y: 540, count: 40, speed: 1.2 });
      } else {
        instructions.setInstruction(
          'NOBODY GOT IT',
          `The answer was: <code>${escapeInline(msg.display_answer)}</code>`,
          'default',
        );
      }
      break;
    case 'score_update':
      leaderboard.setRows(msg.leaderboard_top10);
      sideLeaderboard.setRows(msg.leaderboard_top10);
      // Cache for the podium scene so it can render top-3 on game-over.
      (window as unknown as { __sj_lastLb?: typeof msg.leaderboard_top10 }).__sj_lastLb = msg.leaderboard_top10;
      clueZoom.hide();
      answerReveal.hide();
      // Mark the just-played cell as consumed so it stays dim for the rest of
      // the round. score_update is the right hook (not answer_reveal) because
      // by here the clue is fully scored and we're about to return to the
      // category poll. The board scene preserves the consumed flag locally;
      // subsequent state_snapshot frames don't disturb it (populateBoard only
      // runs on round_change → first poll_open of the round).
      if (lastArmedCell) {
        board.consumeTile(lastArmedCell.col, lastArmedCell.row);
        lastArmedCell = null;
      }
      sfx.play('score-whoosh');
      instructions.setInstruction('PICK A CLUE', 'Viewers vote on the category, then the point value', 'default');
      // Between clues we hide the countdown — there's no phase timing running.
      countdown.hide();
      break;
    case 'state_snapshot': {
      const snapSettings = (msg.state as any).settings;
      const nextShow = snapSettings && typeof snapSettings.show_watch_qr === 'boolean' ? snapSettings.show_watch_qr : true;
      if (nextShow !== showWatchQr) {
        showWatchQr = nextShow;
        applyWatchQrVisibility(currentHandle);
      }
      // Optimistic unlock — Chromium with high media-engagement may permit
      // resuming the AudioContext without an explicit user gesture, and OBS
      // page-permissions count as one. Failures are silent. In viewer mode
      // the iframe is muted by design so we skip the unlock entirely.
      if (!isViewer && !audioBus.isUnlocked()) void audioBus.unlock();
      const s = msg.state;
      if (s.leaderboard_top10) {
        sideLeaderboard.setRows(s.leaderboard_top10);
        (window as unknown as { __sj_lastLb?: typeof s.leaderboard_top10 }).__sj_lastLb = s.leaderboard_top10;
      }
      // Gate lobby-lb writes on roster change; snapshots arrive often, pre-filter cuts thrash.
      const nextLb = s.leaderboard_all_time ?? s.leaderboard_top10 ?? [];
      if (nextLb !== lastLobbyLb) {
        lobby.setLeaderboard(nextLb);
        lastLobbyLb = nextLb;
      }
      applyTheme(s.theme ?? DEFAULT_THEME);
      break;
    }
    case 'poll_open': {
      const endsLocal = wsClient ? wsClient.clock.toLocalMs(msg.ends_at_ms) : msg.ends_at_ms;
      // countdown_starts_at_ms is optional (v1 servers omit it); when present
      // the top-left timer stays hidden until that local time so viewers have
      // a "read" window before the clock pressures them.
      const startsLocal = msg.countdown_starts_at_ms != null
        ? (wsClient ? wsClient.clock.toLocalMs(msg.countdown_starts_at_ms) : msg.countdown_starts_at_ms)
        : undefined;
      pollOverlay.setPhase(msg.phase);
      pollOverlay.hide();
      if (msg.phase === 'category') {
        valueVote.close();
        categoryVote.open(msg.options);
        const live = msg.options.filter((o) => !o.used).length || msg.options.length;
        instructions.setInstruction('VOTE FOR CATEGORY', `Type <code>1</code>-<code>${live}</code> in chat to vote`, 'vote');
        countdown.setPhase('Vote', endsLocal, startsLocal);
      } else {
        categoryVote.close();
        valueVote.open(msg.options);
        const live = msg.options.filter((o) => !o.used).length || msg.options.length;
        instructions.setInstruction('PICK A POINT VALUE', `Type <code>1</code>-<code>${live}</code> or the amount (e.g. <code>600</code>)`, 'vote');
        countdown.setPhase('Pick', endsLocal, startsLocal);
      }
      if (msg.phase === 'category' && !boardHasContent) {
        populateBoard(msg.options.map((o) => o.label), store.getState().round);
      }
      break;
    }
    case 'wi_round_categories': {
      // Mid-game rehydrate path: the rehydrate cache evicts poll_open on
      // poll_close, so a viewer joining after the first category vote
      // closes never received the column names. The backend now emits this
      // frame at round start (and the cache replays it on viewer connect).
      // Idempotent with the poll_open(category) path above — populateBoard
      // is gated by `!boardHasContent` so the OBS source which paints from
      // poll_open first will treat the rehydrated frame as a no-op.
      if (!boardHasContent) {
        populateBoard(msg.categories, msg.round);
      }
      break;
    }
    case 'poll_tally':
      categoryVote.setTallies(msg.tallies);
      valueVote.setTallies(msg.tallies);
      break;
    case 'poll_tally_update':
      categoryVote.setTallies(msg.counts);
      valueVote.setTallies(msg.counts);
      break;
    case 'poll_close': {
      // Close ONLY the overlay whose phase closed; closing both would hide
      // the just-opened next-phase overlay 1.5s later ("disappears fast" bug).
      if (msg.phase === 'category') {
        categoryVote.setWinner(msg.winner);
        const opt = (store.getState().poll?.options ?? []).find((o) => o.key === msg.winner);
        pickedCategory = opt ? opt.label : null;
        setTimeout(() => categoryVote.close(), 1500);
      } else {
        valueVote.setWinner(msg.winner);
        setTimeout(() => valueVote.close(), 1500);
      }
      break;
    }
    case 'round_change':
      // Round change -> reset tiles so the next category poll rebuilds them.
      // Single-round mode: only r1 + results are routed.
      if (msg.round !== lastRound) {
        boardHasContent = false;
        board.resetTiles();
        lastRound = msg.round;
      }
      if (msg.round === 'r1') {
        // Start the looping in-game bed. The one-shot transition sting is
        // dropped here (it would cut off the bed it's meant to introduce);
        // the sparkle burst + on-screen ROUND 1 card already announces the round.
        if (!isViewer) music.play('round');
        instructions.setInstruction('ROUND 1', '5 categories · 5 values each — let the games begin', 'default');
        sparkles.burst({ x: 960, y: 540, count: 36, speed: 1.4 });
      } else if (msg.round === 'results') {
        if (!isViewer) music.play('podium');
        instructions.setInstruction('GAME OVER', 'Thanks for playing!', 'celebrate');
        sparkles.confetti({ count: 160 });
        countdown.hide();
      }
      break;
    case 'wi_new_board': {
      // Continuous-play board reshuffle: backend exhausted the previous 25
      // cells and is about to ship a fresh `poll_open` of phase=category. We
      // splash, wipe the in-memory board state, and let the next poll_open
      // repopulate via populateBoard's `!boardHasContent` gate. board_index
      // is 1-based on the wire (0 = first board, never sent — only reshuffles
      // emit), so the splash reads "BOARD 2", "BOARD 3", etc.
      const ofSuffix = msg.boards_total > 0 ? ` of ${msg.boards_total}` : '';
      const headline = `BOARD ${msg.board_index + 1}${ofSuffix}`;
      roundSplash.show('NEW BOARD', headline);
      sparkles.burst({ x: 960, y: 540, count: 60, speed: 1.6 });
      sfx.play('score-whoosh');
      // Reset local board state so the next category poll's options trigger
      // populateBoard() with the new categories. resetTiles() clears the
      // consumed-cell dim so the fresh board reads as untouched.
      boardHasContent = false;
      board.resetTiles();
      lastArmedCell = null;
      pickedCategory = null;
      break;
    }
    case 'wi_guess_received': guessBubbles.spawn(msg.guess); break;
    case 'cross_game_conflict':
      // Banner only matters for the slug being attempted OR the slug currently
      // running — other game scenes shouldn't surface the warning.
      if (msg.attempted_slug === MY_SLUG || msg.running_slug === MY_SLUG) {
        conflictBanner.show(msg.message);
      }
      break;
    case 'calibration_prompt':
      calibration.show();
      calibration.setCounts(0, 0, 0);
      calibration.setResult(null, 0);
      sfx.play('calibration');
      break;
    case 'calibration_result':
      calibration.setResult(msg.measured_delay_ms, msg.samples);
      setTimeout(() => calibration.hide(), 3000);
      break;
    case 'auth_ok' as OutMsg['type']: {
      const authMsg = msg as unknown as { type: 'auth_ok'; handle: string | null };
      currentHandle = authMsg.handle;
      applyWatchQrVisibility(authMsg.handle);
      break;
    }
    default:
      break;
  }
}

function applyScene(next: SceneName): void {
  (window as unknown as { __sj_scene?: SceneName }).__sj_scene = next;
  root.setAttribute('data-scene', next);
  applySceneVisibility(next, { board, leaderboard, podium, roundTransition, sideLeaderboard, lobby, instructions, connectionLost });
}

function reanchorCenteredScenes(cx: number, cy: number): void {
  const all = [clueZoom.group, answerReveal.group, roundTransition.group,
    podium.group, calibration.group];
  for (const g of all) g.position.set(cx, cy, g.position.z);
}

function applyCameraLayout(pos: CameraPosition): void {
  const camRect = getCameraRect(pos);
  const lbRect = getLeaderboardRect(pos);
  cameraPlaceholder.setRect(camRect);
  cameraPlaceholder.setVisible(devOverlay && camRect !== null);
  leaderboard.setColumnRect(lbRect);
  // Board + pollOverlay pinned to stage coords; cinematics use safe-area center.
  const c = centerOf(computeSafeArea(pos));
  // Board sits left-of-center so it balances vs the right-side LB panel.
  // Apr 2026 iteration: -280 too crowded; -370 truncated money tiles off the
  // left edge. -320 lands centered in the LB-clear content strip.
  board.group.position.set(-320, -80, 0);
  pollOverlay.group.position.set(-320, 0, 5);
  reanchorCenteredScenes(c.x, c.y);
}

// Seed initial camera from URL override; per-tenant setting lands via state_snapshot.
if (camOverride) store.dispatch({ type: 'ws', msg: makeCamSnapshot(camOverride) });

function makeCamSnapshot(pos: CameraPosition): OutMsg {
  // Minimal synthetic state_snapshot to push the override through the reducer.
  return {
    type: 'state_snapshot',
    state: {
      tenant_id: store.getState().tenantId,
      round: store.getState().round === 'lobby' ? 'r1' : (store.getState().round as 'r1' | 'r2' | 'final' | 'results'),
      state: 'LOBBY',
      server_ts: Date.now(),
      broadcast_delay_seconds: store.getState().broadcastDelaySeconds,
      settings: { camera_position: pos },
      leaderboard_top10: store.getState().leaderboard,
    },
  };
}

store.subscribe((next, prev) => {
  if (next.scene !== prev.scene) applyScene(next.scene);
  // FSM is source of truth: leaving a poll phase tears down the overlay.
  if (prev.scene === 'poll_category' && next.scene !== 'poll_category') categoryVote.close();
  if (prev.scene === 'poll_clue_value' && next.scene !== 'poll_clue_value') valueVote.close();
  if (next.broadcastDelaySeconds !== prev.broadcastDelaySeconds) {
    delayIndicator.setDelay(next.broadcastDelaySeconds);
  }
  if (next.leaderboard !== prev.leaderboard) {
    leaderboard.setRows(next.leaderboard);
  }
  // URL override always wins; otherwise track the per-tenant setting.
  const effective = camOverride ?? next.cameraPosition;
  const prevEffective = camOverride ?? prev.cameraPosition;
  if (effective !== prevEffective) applyCameraLayout(effective);
});
applyScene(state0.scene);
delayIndicator.setDelay(state0.broadcastDelaySeconds);
applyCameraLayout(camOverride ?? state0.cameraPosition);

// Per-frame ticks for all animated scenes.
stage.addTick((_dt, _now) => {
  const nowMs = performance.now();
  board.tick(nowMs);
  pollOverlay.tick(nowMs);
  clueZoom.tick(nowMs);
  answerReveal.tick(nowMs);
  leaderboard.tick(nowMs);
  roundTransition.tick(nowMs);
  podium.tick(nowMs);
});

// --- Connect ---
function connectWs(): void {
  // Viewer mode routes through the public read-only feed; streamer mode keeps
  // the existing /browser?key=... handshake. wsUrlFor handles the URL split
  // and WsClient's `viewer` flag suppresses hello/heartbeat client→server.
  const wsUrl = wsUrlFor(viewerMode, resolveWsBase(wsPort));
  wsClient = new WsClient({
    url: wsUrl,
    sessionToken: key,
    viewer: isViewer,
    onMessage: handleWs,
    onState: (s) => {
      store.dispatch({ type: 'connection', value: s });
      // The watch SPA owns the connection-lost UI for viewer iframes — keep
      // the in-iframe overlay suppressed so it doesn't double up with the
      // SPA shell's reconnect spinner.
      if (!isViewer) connectionLost.setState(s);
    },
    onSnap: (prev, next) => {
      console.info(`[clock] drift snap: ${prev.toFixed(1)} -> ${next.toFixed(1)}`);
    },
  });
  wsClient.connect();
  (window as unknown as Record<string, unknown>).__sj_ws = wsClient;
}

if (mockMode) {
  const bus = createMockBus();
  bus.addListener(handleWs);
  store.dispatch({ type: 'connection', value: 'open' });
  connectionLost.setState('open');
  bus.runScript();
  (window as unknown as Record<string, unknown>).__sj_mock = bus;
} else {
  connectWs();
}

// Auto-pause when the browser source is not visible (OBS scene switched away,
// browser tab backgrounded). Closing the WS gracefully lets the backend's
// browser-source-registry fire the soft-abort grace timer; on revisible we
// reconnect and the FSM re-broadcasts state. Mock mode skips this because
// there's no real socket to manage.
if (!mockMode) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Graceful close — code 1000 + reason so the server logs read sensibly.
      // WsClient.close() also flips destroyed=true so the auto-reconnect timer
      // doesn't fight us; we recreate a fresh client on revisible.
      if (wsClient) {
        try { wsClient.close(); } catch { /* swallow */ }
        wsClient = null;
      }
    } else if (document.visibilityState === 'visible') {
      if (!wsClient) connectWs();
    }
  });
}

(window as unknown as Record<string, unknown>).__sj_store = store;
// Expose handler so screenshot harnesses can inject synthetic frames.
(window as unknown as Record<string, unknown>).__sj_handleWs = handleWs;
void BOARD_COLS_MAX; void BOARD_ROWS; void Object3D; void (null as unknown as GameState);
